import axios from 'axios';
import { API_URL } from '../constants/api';
import {
  Field,
  FieldDictionary,
  RegisterFieldRequest,
  LoginFieldRequest
} from '../types/field';
import { universalAuthService } from './universalAuthService';

// Вспомогательная функция для декодирования JWT токена
const decodeJWTPayload = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return {};
  }
};

interface FieldAuthResponse {
  token: string;
  refreshToken: string;
  expiresAt: string;
  emailVerified: boolean;
}

interface RegistrationResponse {
  success: boolean;
  message: string;
  email: string;
}

// Создаем отдельный экземпляр axios для полей
const fieldApiClient = axios.create({
  baseURL: API_URL,
});

// Настраиваем перехватчик для добавления токена поля в заголовки
fieldApiClient.interceptors.request.use((config) => {
  const fieldToken = localStorage.getItem('fieldToken');
  if (fieldToken) {
    config.headers.Authorization = `Bearer ${fieldToken}`;
  }
  return config;
});

// Response interceptor для автоматического обновления токена при 401
fieldApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Если получили 401 и ещё не пробовали обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      console.log('[Field] 401 error detected, attempting token refresh...');

      try {
        const { refreshAccessToken } = await import('./authService');
        const newTokens = await refreshAccessToken('field');

        if (newTokens) {
          console.log('[Field] Token refresh successful, retrying request...');

          // Обновляем токены в localStorage
          universalAuthService.updateTokens('field', newTokens.token, newTokens.refreshToken);

          // Обновляем заголовок Authorization в оригинальном запросе
          originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;

          // Повторяем оригинальный запрос
          return fieldApiClient(originalRequest);
        } else {
          console.log('[Field] Token refresh failed');
          // Очищаем данные поля
          universalAuthService.clearData('field');
          // Перенаправляем на главную
          window.location.href = '/';
        }
      } catch (refreshError) {
        console.error('[Field] Token refresh error:', refreshError);
        universalAuthService.clearData('field');
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Функция регистрации поля (пока используем старое API, так как унифицированное не поддерживает регистрацию полей)
export const registerField = async (request: RegisterFieldRequest): Promise<RegistrationResponse> => {
  try {
    const response = await fieldApiClient.post('/api/field/register', request);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Field registration failed');
  }
};

// Функция авторизации поля через унифицированное API
export const loginField = async (request: LoginFieldRequest): Promise<FieldAuthResponse & { fieldData: Field }> => {
  try {
    const unifiedRequest = {
      email: request.email,
      password: request.password
    };
    
    const response = await axios.post(`${API_URL}/api/auth/login`, unifiedRequest);
    
    // Унифицированное API возвращает { token: string, user: fieldData, message: string }
    const token = response.data.token;
    const fieldData = response.data.user;
    const tokenPayload = decodeJWTPayload(token);
    
    // Проверяем, что это действительно поле
    if (tokenPayload.role !== 'field') {
      throw new Error('Неверные учётные данные для поля');
    }
    
    return {
      token: token,
      refreshToken: response.data.refreshToken,
      expiresAt: new Date(tokenPayload.exp * 1000).toISOString(),
      emailVerified: fieldData.isEmailVerified || false,
      fieldData: fieldData
    };
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Field login failed');
  }
};


// Функция получения профиля поля
export const getFieldProfile = async (): Promise<Field> => {
  try {
    const response = await fieldApiClient.get('/api/field/profile');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field profile');
  }
};

// Функция получения списка одобренных полей из справочника
export const getFieldsDictionary = async (): Promise<FieldDictionary[]> => {
  try {
    const response = await fieldApiClient.get('/api/field/dictionary');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get fields dictionary');
  }
};

// Функция получения списка зарегистрированных полей (field_document)
export const getRegisteredFields = async (): Promise<Field[]> => {
  try {
    const response = await fieldApiClient.get('/api/field/registered');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get registered fields');
  }
};


// Публичная функция получения игр поля (без авторизации поля)
export const getPublicFieldGames = async (
  fieldId: string,
  gameType: 'airsoft' | 'paintball' | 'speedball',
  options?: {
    page?: number;
    pageSize?: number;
    specificDate?: string;
  }
): Promise<{
  games: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}> => {
  try {
    const params = new URLSearchParams();

    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.specificDate) params.append('specificDate', options.specificDate);

    const queryString = params.toString();
    const url = `${API_URL}/api/field/${fieldId}/games/${gameType}${queryString ? `?${queryString}` : ''}`;

    // Используем axios без авторизации поля
    const response = await axios.get(url);
    return response.data;
  } catch (error: any) {
    // Если 404 - возвращаем пустой результат вместо ошибки
    if (error.response?.status === 404) {
      return {
        games: [],
        totalCount: 0,
        page: 1,
        pageSize: options?.pageSize || 10,
        totalPages: 0,
        hasMore: false
      };
    }
    throw new Error(error.response?.data?.message || `Failed to get field ${gameType} missions`);
  }
};

// Универсальная функция получения игр поля с пагинацией и фильтрацией (для авторизованного поля)
export const getFieldGames = async (
  fieldId: string, 
  gameType: 'airsoft' | 'paintball' | 'speedball',
  options?: {
    page?: number;
    pageSize?: number;
    specificDate?: string;
  }
): Promise<{
  games: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}> => {
  try {
    const params = new URLSearchParams();
    
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.specificDate) params.append('specificDate', options.specificDate);
    
    const queryString = params.toString();
    const url = `/api/field/${fieldId}/games/${gameType}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fieldApiClient.get(url);
    return response.data;
  } catch (error: any) {
    // Если 404 - возвращаем пустой результат вместо ошибки
    if (error.response?.status === 404) {
      return {
        games: [],
        totalCount: 0,
        page: 1,
        pageSize: options?.pageSize || 10,
        totalPages: 0,
        hasMore: false
      };
    }
    throw new Error(error.response?.data?.message || `Failed to get field ${gameType} missions`);
  }
};



// Обновление информации о поле
export const updateFieldInfo = async (fieldInfo: { 
  name: string; 
  address: string; 
  description: string; 
  phone: string; 
  website: string; 
  supportedGameTypes: string[];
  state: string;
}): Promise<void> => {
  try {
    const response = await fieldApiClient.put('/api/field/info', fieldInfo);
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update field information');
  }
};

// Получение топ полей
export const getTopFields = async (count: number = 10, gameType?: string): Promise<Field[]> => {
  try {
    const params = new URLSearchParams();
    params.append('count', count.toString());
    if (gameType) {
      params.append('gameType', gameType);
    }
    const response = await fieldApiClient.get(`/api/field/top?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get top fields');
  }
};

// Получение полей по штату
export const getFieldsByState = async (state: string, count: number = 50, gameType?: string): Promise<Field[]> => {
  try {
    const params = new URLSearchParams();
    params.append('count', count.toString());
    if (gameType) {
      params.append('gameType', gameType);
    }
    const response = await fieldApiClient.get(`/api/field/by-state/${encodeURIComponent(state)}?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get fields by state');
  }
};

// Получение публичной информации о поле по ID (без авторизации)
export const getPublicFieldById = async (fieldId: string): Promise<Field> => {
  try {
    const response = await axios.get(`${API_URL}/api/field/public/${fieldId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field information');
  }
};

// Поиск полей по названию
export const searchFields = async (searchTerm: string, count: number = 20, gameType?: string): Promise<Field[]> => {
  try {
    const params = new URLSearchParams();
    params.append('term', searchTerm);
    params.append('count', count.toString());
    if (gameType) {
      params.append('gameType', gameType);
    }
    const response = await fieldApiClient.get(`/api/field/search?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to search fields');
  }
};

// ========== Field Maps API ==========

export interface FieldMapDevice {
  deviceId: string;
  name: string;
  type: string;
  picoId: string;
  x: number;
  y: number;
  properties: Record<string, string>;
}

// Schema element type for visual map editor
export interface SchemaElement {
  id: string;
  type: 'rect' | 'circle' | 'line' | 'polygon' | 'freehand' | 'text' | 'icon' | 'road' | 'trail';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  points?: number[];
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  text?: string;
  fontSize?: number;
  label?: string;
  iconType?: string;
  rotation?: number;
  locked?: boolean;
}

export interface MissionStatistics {
  totalMissions: number;
  airsoftMissions: number;
  paintballMissions: number;
  speedballMissions: number;
}

export interface FieldMap {
  id: string;
  name: string;
  fieldId: string;
  description: string;
  // Схема карты
  schemaImageUrl: string;
  schemaVideoUrl: string;
  // Общие фото карты (до 5)
  images: string[];
  // Видео с YouTube
  youtubeVideos: string[];
  isActive: boolean;
  devices: FieldMapDevice[];
  deviceCount: number;
  fieldNodesIds: string[];
  // Visual schema elements (drawn on canvas)
  schemaElements?: SchemaElement[];
  // Exported map image URL
  exportedImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  // Backward compatibility
  imageUrl?: string;
  // Mission statistics
  missionStatistics?: MissionStatistics;
}

export interface FieldMapPayload {
  name: string;
  fieldId: string;
  description: string;
  schemaImageUrl?: string;
  schemaVideoUrl?: string;
  images?: string[];
  youtubeVideos?: string[];
  devices: FieldMapDevicePayload[];
}

export interface FieldMapDevicePayload {
  name: string;
  type: string;
  picoId: string;
  x: number;
  y: number;
  properties: Record<string, string>;
}

// Получение карт для авторизованного поля (с данными о нодах)
export const getMyFieldMaps = async (): Promise<FieldMap[]> => {
  try {
    const response = await fieldApiClient.get('/api/field/maps/my');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field maps');
  }
};

// Получение карт поля (публичный, без данных о нодах)
export const getPublicFieldMaps = async (fieldId: string): Promise<FieldMap[]> => {
  try {
    const response = await axios.get(`${API_URL}/api/field/maps/field/${fieldId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field maps');
  }
};

// Получение одной карты по ID (публичный)
export const getPublicFieldMapById = async (mapId: string): Promise<FieldMap> => {
  try {
    const response = await axios.get(`${API_URL}/api/field/maps/${mapId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field map');
  }
};

// Получение шаблонов операций для карты (публичный)
export interface PublicOperationTemplate {
  id: string;
  fieldId: string;
  mapId: string;
  operationFieldConfigsId: string;
  operationName: string;  // Название операции для группировки в UI
  name: string;
  description: string;
  devices: PublicTemplateDevicePosition[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicTemplateDevicePosition {
  entryId: string;
  configId: string;
  deviceCatalogId: string;
  deviceName: string;
  deviceType: string;
  model: string;
  iconUrl?: string;
  positionX: number;
  positionY: number;
  displayNumber: number;
  scale: number;
  locked: boolean;
}

export const getPublicOperationTemplates = async (mapId: string): Promise<PublicOperationTemplate[]> => {
  try {
    const response = await axios.get(`${API_URL}/api/field/maps/${mapId}/operation-templates`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to get operation templates:', error);
    return []; // Возвращаем пустой массив при ошибке
  }
};

// Создание карты
export const createFieldMap = async (
  payload: FieldMapPayload,
  schemaImage?: File,
  images?: File[]
): Promise<FieldMap> => {
  try {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('fieldId', payload.fieldId);
    formData.append('description', payload.description || '');

    if (payload.schemaVideoUrl) {
      formData.append('schemaVideoUrl', payload.schemaVideoUrl);
    }

    if (payload.youtubeVideos && payload.youtubeVideos.length > 0) {
      payload.youtubeVideos.forEach((url, index) => {
        formData.append(`youtubeVideos[${index}]`, url);
      });
    }

    if (payload.devices && payload.devices.length > 0) {
      formData.append('devices', JSON.stringify(payload.devices));
    }

    if (schemaImage) {
      formData.append('schemaImage', schemaImage);
    }

    if (images && images.length > 0) {
      images.forEach((img) => {
        formData.append('images', img);
      });
    }

    const response = await fieldApiClient.post('/api/field/maps', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error: any) {
    const data = error.response?.data;
    // Handle validation errors format
    if (data?.errors) {
      const errorMessages = Object.entries(data.errors)
        .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
        .join('; ');
      throw new Error(errorMessages);
    }
    throw new Error(data?.message || data?.title || 'Failed to create map');
  }
};

// Обновление карты
export const updateFieldMap = async (
  id: string,
  payload: FieldMapPayload,
  schemaImage?: File,
  newImages?: File[],
  imagesToDelete?: string[]
): Promise<FieldMap> => {
  try {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('fieldId', payload.fieldId);
    formData.append('description', payload.description || '');

    if (payload.schemaVideoUrl) {
      formData.append('schemaVideoUrl', payload.schemaVideoUrl);
    }

    if (payload.youtubeVideos && payload.youtubeVideos.length > 0) {
      payload.youtubeVideos.forEach((url, index) => {
        formData.append(`youtubeVideos[${index}]`, url);
      });
    }

    if (payload.devices && payload.devices.length > 0) {
      formData.append('devices', JSON.stringify(payload.devices));
    }

    if (schemaImage) {
      formData.append('schemaImage', schemaImage);
    }

    if (newImages && newImages.length > 0) {
      newImages.forEach((img) => {
        formData.append('newImages', img);
      });
    }

    if (imagesToDelete && imagesToDelete.length > 0) {
      imagesToDelete.forEach((url, index) => {
        formData.append(`imagesToDelete[${index}]`, url);
      });
    }

    const response = await fieldApiClient.put(`/api/field/maps/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update map');
  }
};

// Удаление карты
export const deleteFieldMap = async (id: string): Promise<void> => {
  try {
    await fieldApiClient.delete(`/api/field/maps/${id}`);
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete map');
  }
};

// ========== Field Nodes API ==========

export interface FieldNode {
  id: string;
  saleNumber: string;
  nodeCode: string; // Уникальный номер для привязки к карте
  nodeType: string;
  fieldId?: string;
  mapId?: string;
  position?: string;
  friendlyName: string;
  soldAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Получение узлов поля
export const getFieldNodes = async (fieldId: string): Promise<FieldNode[]> => {
  try {
    const response = await fieldApiClient.get(`/api/field/nodes/field/${fieldId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field nodes');
  }
};

// Поиск узла по nodeCode
export const getNodeByNodeCode = async (nodeCode: string): Promise<FieldNode> => {
  try {
    const response = await fieldApiClient.get(`/api/field/nodes/by-node-code/${encodeURIComponent(nodeCode)}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Node not found');
  }
};

// Получение узлов карты
export const getMapNodes = async (mapId: string): Promise<FieldNode[]> => {
  try {
    const response = await fieldApiClient.get(`/api/field/nodes/map/${mapId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get map nodes');
  }
};

// Привязка узла к карте
export const assignNodeToMap = async (nodeId: string, mapId: string, position?: string): Promise<FieldNode> => {
  try {
    const response = await fieldApiClient.put(`/api/field/nodes/${nodeId}/assign-map`, {
      mapId,
      position
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to assign node to map');
  }
};

// Отвязка узла от карты
export const unassignNodeFromMap = async (nodeId: string): Promise<FieldNode> => {
  try {
    const response = await fieldApiClient.put(`/api/field/nodes/${nodeId}/unassign-map`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to unassign node from map');
  }
};

// Обновление узла
export const updateNode = async (nodeId: string, friendlyName: string, position?: string): Promise<FieldNode> => {
  try {
    const response = await fieldApiClient.put(`/api/field/nodes/${nodeId}`, {
      friendlyName,
      position
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update node');
  }
};

// ========== Field Office Node (Main Node) API ==========

export interface FieldOfficeNode {
  id: string;
  fieldId: string;
  fieldNodeId: string;
  name: string;
  description: string;
  saleNumber: string;
  nodeCode: string;
  nodeType: string;
  friendlyName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Получение главного узла поля
export const getFieldOfficeNode = async (): Promise<FieldOfficeNode | null> => {
  try {
    const response = await fieldApiClient.get('/api/field/office-node');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error(error.response?.data?.message || 'Failed to get main node');
  }
};

// Назначение главного узла поля по nodeCode
export const assignOfficeNode = async (nodeCode: string, name: string, description?: string): Promise<FieldOfficeNode> => {
  try {
    const response = await fieldApiClient.post('/api/field/office-node', {
      nodeCode,
      name,
      description
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to assign main node');
  }
};

// Обновление главного узла поля
export const updateOfficeNode = async (name: string, description?: string): Promise<FieldOfficeNode> => {
  try {
    const response = await fieldApiClient.put('/api/field/office-node', {
      name,
      description
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update main node');
  }
};

// Удаление главного узла поля
export const removeOfficeNode = async (): Promise<void> => {
  try {
    await fieldApiClient.delete('/api/field/office-node');
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to remove main node');
  }
};

// ========== Map Games API ==========

// Получение игр для конкретной карты
export const getMapGames = async (
  mapId: string,
  gameType: 'airsoft' | 'paintball' | 'speedball',
  options?: {
    page?: number;
    pageSize?: number;
    specificDate?: string;
  }
): Promise<{
  games: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}> => {
  try {
    const params = new URLSearchParams();

    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.specificDate) params.append('specificDate', options.specificDate);

    const queryString = params.toString();
    const url = `/api/field/maps/${mapId}/games/${gameType}${queryString ? `?${queryString}` : ''}`;

    const response = await axios.get(`${API_URL}${url}`);
    return response.data;
  } catch (error: any) {
    // Если 404 - возвращаем пустой результат вместо ошибки
    if (error.response?.status === 404) {
      return {
        games: [],
        totalCount: 0,
        page: 1,
        pageSize: options?.pageSize || 10,
        totalPages: 0,
        hasMore: false
      };
    }
    throw new Error(error.response?.data?.message || `Failed to get map ${gameType} missions`);
  }
};

// ========== Field Owned Operations API ==========

export interface FieldOperationDevice {
  fmdId: string;           // field_mission_devices ID
  deviceCode: string;      // Код для маркировки устройства
  deviceType: string;
  model: string;
  iconUrl: string;
}

export interface FieldOperation {
  id: string;  // configId
  operationName: string;
  uniqueCode: string;  // Код операции из каталога
  configUniqueCode: string;  // Уникальный код покупки для идентификации
  defaultDuration: number | null;
  winPoints: number | null;
  losePoints: number | null;
  deviceCount: number;
  devices: FieldOperationDevice[];
  purchasedAt: string;
  activationDate: string | null;
  status: string; // Pending, Testing, Active
}

// Получение всех операций поля
export const getMyOperations = async (): Promise<FieldOperation[]> => {
  try {
    const response = await fieldApiClient.get('/api/field/operations');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get operations');
  }
};

// Получение активных операций поля
export const getMyActiveOperations = async (): Promise<FieldOperation[]> => {
  try {
    const response = await fieldApiClient.get('/api/field/operations/active');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get active operations');
  }
};

// Получение конкретной операции по ID
export const getOperationById = async (id: string): Promise<FieldOperation> => {
  try {
    const response = await fieldApiClient.get(`/api/field/operations/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get operation');
  }
};

// ========== Field Owned Operations (Grouped) API ==========

export interface FieldOwnedOperation {
  operationId: string;
  operationName: string;
  count: number;
  firstPurchasedAt: string;
  totalDevices: number;
  winPoints: number | null;
  losePoints: number | null;
}

// Получение сгруппированных операций поля (оптимизированный запрос) - для авторизованного поля
export const getOwnedOperations = async (): Promise<FieldOwnedOperation[]> => {
  try {
    const response = await fieldApiClient.get('/api/field/operations/owned');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get owned operations');
  }
};

// Публичное получение операций поля по ID (без авторизации)
export const getPublicFieldOperations = async (fieldId: string): Promise<FieldOwnedOperation[]> => {
  try {
    const response = await axios.get(`${API_URL}/api/field/public/${fieldId}/operations`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get field operations');
  }
};

// ========== Operation Devices API (grouped by operationId) ==========

export interface OperationDeviceInfo {
  configId: string;
  deviceIndex: number;
  entryId: string;
  deviceCatalogId: string;
  friendlyName: string;
  deviceType: string;
  model: string;
}

export interface OperationDevicesResponse {
  operationId: string;
  operationName: string;
  uniqueCode: string;
  defaultDuration: number | null;
  winPoints: number | null;
  losePoints: number | null;
  totalDevices: number;
  configCount: number;
  devices: OperationDeviceInfo[];
}

// Получение всех устройств для операции (по operationId, группировка всех покупок)
export const getDevicesByOperationId = async (operationId: string): Promise<OperationDevicesResponse> => {
  try {
    const response = await fieldApiClient.get(`/api/field/operations/by-operation/${operationId}/devices`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get operation devices');
  }
};

